import { useState } from 'react';
import { MARKETING_FEE, MIN_MARGIN, TARGET_MARGIN } from '../utils/prebid';
import { PREBID_CSS, money, pct } from '../components/PrebidResult';

/* Pre-Bid (Manual) — for products with no squares or measurements.
   Type the sale price and every cost yourself: labor, materials, commission, other.
   The 2.7% marketing fee is added from the sale price, the same as the regular
   Pre-Bid Calculator. Same verdict: at least 40% margin; plus the price for 55%.
   Both "price for" figures keep these same costs (cost ÷ (1 − margin)).          */

const num = v => { const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(n) && n > 0 ? n : 0; };
const MIN = Math.round(MIN_MARGIN * 100);
const GOAL = Math.round(TARGET_MARGIN * 100);

const FIELDS = [
  ['labor', 'Labor ($)'],
  ['materials', 'Materials ($)'],
  ['commission', 'Commission ($)'],
  ['other', 'Other costs ($)'],
];

export default function ManualPrebid() {
  const [v, setV] = useState({ product: '', sale: '', labor: '', materials: '', commission: '', other: '' });
  const set = (k, x) => setV(s => ({ ...s, [k]: x }));

  const sale = num(v.sale);
  const parts = FIELDS.map(([k, label]) => ({ k, label: label.replace(' ($)', ''), amount: num(v[k]) }));
  const typed = parts.reduce((s, p) => s + p.amount, 0);
  const marketing = sale * MARKETING_FEE;
  const totalCost = typed + marketing;
  const profit = sale - totalCost;
  const margin = sale ? profit / sale : 0;
  const ready = sale > 0 && typed > 0;
  const ok = margin >= MIN_MARGIN;
  const minSale = totalCost / (1 - MIN_MARGIN);
  const targetSale = totalCost / (1 - TARGET_MARGIN);

  return (
    <>
      <style>{PREBID_CSS}</style>
      <div className="page-header">
        <h2>Pre-Bid Calculator (Manual)</h2>
        <div style={{ fontSize: 13, color: '#888' }}>
          For products with no squares or measurements. Type the sale price and every cost yourself. The {(MARKETING_FEE * 100).toFixed(1)}% marketing fee is added automatically. Every job needs at least a {MIN}% margin.
        </div>
      </div>
      <div className="page-body">
        <div className="pb-grid">
          <div className="pb-field">
            <label>Product / job (optional)</label>
            <input className="form-input" placeholder="e.g. Patio cover" value={v.product} onChange={e => set('product', e.target.value)} />
          </div>
          <div className="pb-field">
            <label>Sale price (total job)</label>
            <input className="form-input" inputMode="decimal" placeholder="e.g. 12,000" value={v.sale} onChange={e => set('sale', e.target.value)} />
          </div>
        </div>
        <div className="pb-grid">
          {FIELDS.map(([k, label]) => (
            <div className="pb-field" key={k}>
              <label>{label}</label>
              <input className="form-input" inputMode="decimal" placeholder="0" value={v[k]} onChange={e => set(k, e.target.value)} />
            </div>
          ))}
        </div>

        {!ready ? (
          <div className="pb-card" style={{ color: '#7a8a82' }}>Enter the sale price and at least one cost to check the margin.</div>
        ) : (
          <>
            <div className={'pb-verdict ' + (ok ? 'ok' : 'bad')}>
              <div className="big">{ok ? '✓ Good to go' : '⚠ Red flag'}: {pct(margin)} margin</div>
              <div className="sub">
                {v.product ? `${v.product}: ` : ''}
                {ok ? `This bid clears the ${MIN}% minimum.` : `Below the ${MIN}% minimum. The price needs to be at least ${money(minSale)}, that's ${money(minSale - sale)} more.`}
              </div>
              <div className="pb-tiles">
                <div className="pb-tile"><div className="k">Sale price</div><div className="v">{money(sale)}</div></div>
                <div className="pb-tile"><div className="k">Total cost</div><div className="v">{money(totalCost)}</div></div>
                <div className="pb-tile"><div className="k">Profit</div><div className="v">{money(profit)}</div></div>
                <div className="pb-tile"><div className="k">Price for {MIN}%</div><div className="v">{money(minSale)}</div></div>
                <div className="pb-tile goal"><div className="k">Price for {GOAL}%</div><div className="v">{money(targetSale)}</div>
                  <div className="s">{margin >= TARGET_MARGIN ? `Already at ${GOAL}% or more` : `${money(targetSale - sale)} more than this bid`}</div></div>
              </div>
            </div>

            <div className="pb-card">
              {parts.filter(p => p.amount).map(p => (
                <div className="pb-row" key={p.k}><span>{p.label}<span className="hint">entered</span></span><b>{money(p.amount)}</b></div>
              ))}
              <div className="pb-row">
                <span>Marketing fee<span className="hint">{(MARKETING_FEE * 100).toFixed(1)}% of {money(sale)}</span></span>
                <b>{money(marketing)}</b>
              </div>
              <div className="pb-row pb-total"><span>Total cost</span><b>{money(totalCost)}</b></div>
              <div className="pb-row">
                <span>Profit<span className="hint">{money(sale)} − {money(totalCost)}</span></span>
                <b>{money(profit)}</b>
              </div>
              <div className="pb-row">
                <span>Margin<span className="hint">profit ÷ sale price · minimum {MIN}%</span></span>
                <b style={{ color: ok ? '#1B5E20' : '#B71C1C' }}>{pct(margin)}</b>
              </div>
            </div>
            <div className="pb-note">Price for {MIN}% and {GOAL}% keep these same costs: total cost ÷ (1 − margin).</div>
          </>
        )}
      </div>
    </>
  );
}
